import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import cn from 'classnames';
import {LocationWithWeather} from '@/app/interfaces/weather';
import {RouteName} from '@/app/constants/routing';
import {useLocalizer} from '@/common/services/localizer';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {temperatureDisplayReduce} from '@/common/helpers/unitReducers/temperatureDisplayReduce';
import {selectDomainConfig, selectTemperatureUnit} from '@/app/store/config/selectors';
import {RouterLink} from '@/common/components/RouterLink';
import {ConditionMediumIcon} from '@/common/components/ConditionMediumIcon';
import {TextLink} from '@/common/components/TextLink';
import styles from './WeatherTopLocations.css';

interface WeatherTopLocationsProps {
	locations: LocationWithWeather[];
}

export const WeatherTopLocations = memo<WeatherTopLocationsProps>(props => {
	const {locations} = props;

	const temperatureUnit = useSelector(selectTemperatureUnit);
	const {domainName} = useSelector(selectDomainConfig);

	const urlBuilder = useUrlBuilder();

	const t = useLocalizer();

	if (!locations.length) {
		return null;
	}

	return (
		<div>
			<h2 className={styles.title}>{t(`topLocationsTitle.${domainName}`)}</h2>
			<ul className={styles.list}>
				{locations.map(({location, weather}) => (
					<li key={location.id} className={styles.entry}>
						<div className={styles.mainRow}>
							<RouterLink
								className={cn(styles.link, location.flags.countryAdminCenter && styles.isMarked)}
								component={TextLink}
								url={urlBuilder(RouteName.weather, {location})}>
								{t('weather')} {location.title}
							</RouterLink>
							<div className={styles.fillRow} />
							{weather && (
								<>
									<p className={styles.temperature}>
										{temperatureDisplayReduce(weather.temperature, temperatureUnit)}
									</p>
									<ConditionMediumIcon
										className={styles.conditionIcon}
										condition={weather.condition}
										isNight={weather.isNight}
									/>
								</>
							)}
						</div>
						<div className={styles.secondaryRow}>
							{weather?.temperatureMin !== undefined && weather.temperatureMax !== undefined && (
								<p className={styles.temperatureRange}>
									{temperatureDisplayReduce(weather.temperatureMin, temperatureUnit)}..
									{temperatureDisplayReduce(weather.temperatureMax, temperatureUnit)}
								</p>
							)}
							<p className={styles.conditionText}>
								{weather && t(`weatherConditions.${weather.condition}`)}
							</p>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
});

WeatherTopLocations.displayName = 'WeatherTopLocations';
